import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";

import { parseGid } from "@shopify/hydrogen-react";
import {
  type CurrencyCode,
  type Maybe,
  type CartLineInput,
  type InputMaybe,
  type CartLineUpdateInput,
  type ProductVariant,
} from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import {
  type LegacyCartAPIParameters,
  legacyCartAPIFetcher,
  legacyCartMutateAPIFetcher,
} from "@react/hooks/fetchers";
import {
  LEGACY_CART_API_URL,
  LEGACY_CART_ADD_API_URL,
  LEGACY_CART_UPDATE_API_URL,
  LEGACY_CART_CLEAR_API_URL,
} from "@root/tablescape/constants";
import { getShopifyGlobalId } from "@tablescape/utilities";

type LegacyCartConnection = {
  attributes: Record<string, string>;
  cart_level_discount_applications: [];
  currency: CurrencyCode;
  item_count: number;
  items: {
    discounted_price: number;
    discounts: [];
    featured_image: {
      alt: string | undefined;
      aspect_ratio: number;
      height: number;
      url: string;
      width: number;
    };
    final_line_price: number;
    final_price: number;
    gift_card: boolean;
    grams?: number;
    handle: string;
    has_components: boolean;
    id: number;
    image: string;
    key: string;
    line_level_discount_allocations: [];
    line_level_total_discount: number;
    line_price: number;
    options_with_values: Record<string, string>[];
    original_line_price: number;
    original_price: number;
    presentment_price: number;
    price: number;
    product_description: string;
    product_has_only_default_variant: boolean;
    product_id: number;
    product_title: string;
    product_type: string;
    properties: Record<string, unknown>;
    quantity: number;
    quantity_rule: {
      increment: number;
      max: Maybe<number>;
      min: number;
    };
    requires_shipping: boolean;
    sku: string;
    taxable: boolean;
    title: string;
    total_discount: number;
    url: string;
    variant_id: number;
    variant_options: string[];
    variant_title: string;
    vendor: string;
  }[];
  items_subtotal_price: string;
  note: Maybe<string>;
  original_total_price: number;
  requires_shipping: boolean;
  token: string;
  total_discount: number;
  total_price: number;
  total_weight: number;
};

type LegacyCartDetails = {
  addToLegacyCart: (
    linesToAdd: InputMaybe<CartLineInput[]> | undefined
  ) => void;
  clearLegacyCart: () => void;
  legacyCartAttributes: Record<string, string> | undefined;
  legacyCartId: string | undefined;
  legacyCartItems: LegacyCartConnection["items"] | null | undefined;
  legacyCartStatus: "fetching" | "idle" | "uninitialized";
  updateToLegacyCart: (linesToUpdate: {
    attributes?: Record<string, string>;
    note?: Maybe<string>;
    updates?: Pick<CartLineUpdateInput, "id" | "merchandiseId" | "quantity">[];
  }) => void;
};

function mapLineAttributesToLegacyCartProperties(
  attributes: CartLineInput["attributes"]
) {
  if (!attributes) {
    return attributes;
  }

  return attributes.reduce((acc, attribute) => {
    return {
      ...acc,
      [attribute.key]: attribute.value,
    };
  }, {});
}

const LegacyCartContext = createContext<LegacyCartDetails | undefined>(
  undefined
);

function LegacyCartProvider({ children }: PropsWithChildren) {
  const [legacyCartItems, setLegacyCartItems] =
    useState<LegacyCartDetails["legacyCartItems"]>();
  const [legacyCartAttributes, setLegacyCartAttributes] =
    useState<LegacyCartDetails["legacyCartAttributes"]>();
  const [legacyCartItemsToAdd, setLegacyCartItemsToAdd] = useState<
    | {
        items: {
          id: ProductVariant["id"];
          quantity: number;
        }[];
      }
    | undefined
  >();

  const [legacyCartItemsToUpdate, setLegacyCartItemsToUpdate] = useState<
    | {
        attributes?: Record<string, string>;
        note?: Maybe<string>;
        updates?: Record<number, number>;
      }
    | undefined
  >();

  const {
    data: legacyCartData,
    isLoading: isLegacyCartDataLoading,
    isValidating: isLegacyCartDataValidating,
  } = useSWR<LegacyCartConnection, Error, Maybe<LegacyCartAPIParameters>>(
    [LEGACY_CART_API_URL, undefined, "GET"],
    legacyCartAPIFetcher,
    {}
  );

  const legacyCartId = useMemo<LegacyCartDetails["legacyCartId"]>(() => {
    if (!legacyCartData?.token) {
      return undefined;
    }

    return getShopifyGlobalId("Cart", legacyCartData?.token);
  }, [legacyCartData?.token]);

  useEffect(() => {
    if (legacyCartData?.items) {
      setLegacyCartItems(legacyCartData.items);
    }
  }, [legacyCartData?.items]);

  useEffect(() => {
    if (legacyCartData?.attributes) {
      setLegacyCartAttributes(legacyCartData.attributes);
    }
  }, [legacyCartData?.attributes]);

  const {
    data: legacyCartAddData,
    isLoading: isLegacyCartAddDataLoading,
    isValidating: isLegacyCartAddDataValidating,
  } = useSWR<LegacyCartConnection>(
    legacyCartItemsToAdd?.items?.length
      ? [LEGACY_CART_ADD_API_URL, legacyCartItemsToAdd]
      : null,
    legacyCartMutateAPIFetcher,
    {}
  );

  useEffect(() => {
    if (legacyCartAddData?.items) {
      setLegacyCartItems(legacyCartAddData.items);
    }
  }, [legacyCartAddData?.items]);

  useEffect(() => {
    if (legacyCartAddData?.attributes) {
      setLegacyCartAttributes(legacyCartAddData.attributes);
    }
  }, [legacyCartAddData?.attributes]);

  const {
    data: legacyCartUpdateData,
    isLoading: isLegacyCartUpdateDataLoading,
    isValidating: isLegacyCartUpdateDataValidating,
  } = useSWR<LegacyCartConnection>(
    () => {
      return Object.keys(legacyCartItemsToUpdate ?? {}).length
        ? [LEGACY_CART_UPDATE_API_URL, legacyCartItemsToUpdate]
        : null;
    },
    legacyCartMutateAPIFetcher,
    {}
  );

  useEffect(() => {
    if (legacyCartUpdateData?.items) {
      setLegacyCartItems(legacyCartUpdateData.items);
    }
  }, [legacyCartUpdateData?.items]);

  useEffect(() => {
    if (legacyCartUpdateData?.attributes) {
      setLegacyCartAttributes(legacyCartUpdateData.attributes);
    }
  }, [legacyCartUpdateData?.attributes]);

  const {
    data: legacyCartClearData,
    isLoading: isLegacyCartClearDataLoading,
    isValidating: isLegacyCartClearDataValidating,
  } = useSWR<LegacyCartConnection>(
    legacyCartItems === null ? [LEGACY_CART_CLEAR_API_URL] : null,
    legacyCartMutateAPIFetcher,
    {}
  );

  useEffect(() => {
    if (legacyCartClearData?.items) {
      setLegacyCartItems(legacyCartClearData.items);
    }
  }, [legacyCartClearData?.items]);

  useEffect(() => {
    if (legacyCartClearData?.attributes) {
      setLegacyCartAttributes(legacyCartClearData.attributes);
    }
  }, [legacyCartClearData?.attributes]);

  const addToLegacyCart = useCallback<LegacyCartDetails["addToLegacyCart"]>(
    (linesToAdd) => {
      if (!linesToAdd) {
        return;
      }

      setLegacyCartItemsToAdd({
        items: linesToAdd.map((line) => ({
          id: parseGid(line.merchandiseId).id,
          quantity: line.quantity ?? 1,
          properties: mapLineAttributesToLegacyCartProperties(line.attributes),
        })),
      });
    },
    []
  );

  const clearLegacyCart = useCallback<
    LegacyCartDetails["clearLegacyCart"]
  >(() => {
    setLegacyCartItems(null);
  }, []);

  const updateToLegacyCart = useCallback<
    LegacyCartDetails["updateToLegacyCart"]
  >((linesToUpdate) => {
    if (!linesToUpdate) {
      return;
    }

    setLegacyCartItemsToUpdate({
      ...linesToUpdate,
      ...(linesToUpdate.updates
        ? {
            updates: linesToUpdate.updates?.reduce((acc, line) => {
              if (!line?.merchandiseId) {
                return acc;
              }

              return {
                ...acc,
                [parseGid(line.merchandiseId).id]: line.quantity,
              };
            }, {}),
          }
        : {}),
    });
  }, []);

  const legacyCartStatus = useMemo<
    LegacyCartDetails["legacyCartStatus"]
  >(() => {
    if (legacyCartItems === undefined) {
      return "uninitialized";
    }
    if (
      isLegacyCartAddDataLoading ||
      isLegacyCartAddDataValidating ||
      isLegacyCartClearDataLoading ||
      isLegacyCartClearDataValidating ||
      isLegacyCartDataLoading ||
      isLegacyCartDataValidating ||
      isLegacyCartUpdateDataLoading ||
      isLegacyCartUpdateDataValidating
    ) {
      return "fetching";
    }

    return "idle";
  }, [
    isLegacyCartAddDataLoading,
    isLegacyCartAddDataValidating,
    isLegacyCartClearDataLoading,
    isLegacyCartClearDataValidating,
    isLegacyCartDataLoading,
    isLegacyCartDataValidating,
    isLegacyCartUpdateDataLoading,
    isLegacyCartUpdateDataValidating,
    legacyCartItems,
  ]);

  const value = useMemo<LegacyCartDetails>(
    () => ({
      addToLegacyCart,
      clearLegacyCart,
      legacyCartAttributes,
      legacyCartId,
      legacyCartItems,
      legacyCartStatus,
      updateToLegacyCart,
    }),
    [
      addToLegacyCart,
      clearLegacyCart,
      legacyCartAttributes,
      legacyCartId,
      legacyCartItems,
      legacyCartStatus,
      updateToLegacyCart,
    ]
  );

  return (
    <LegacyCartContext.Provider value={value}>
      {children}
    </LegacyCartContext.Provider>
  );
}

function useLegacyCart() {
  const context = useContext<LegacyCartDetails | undefined>(LegacyCartContext);

  if (context === undefined) {
    throw new Error("useLegacyCart must be used within a LegacyCartProvider");
  }

  return context;
}

export { LegacyCartProvider, useLegacyCart };
